import { createClient, SupabaseClient, Session, User, AuthChangeEvent } from '@supabase/supabase-js'

const supabaseUrl = process.env.VUE_APP_SUPABASE_URL as string
const supabaseKey = process.env.VUE_APP_SUPABASE_KEY as string

// ── Shared types ──────────────────────────────────────────────────────────────

export interface WishlistItem {
  gameID:      string
  title:       string
  thumb:       string
  storeID:     string
  normalPrice: string
  salePrice:   string
  savings:     string
  dealID:      string
}

export interface DealRow {
  dealID:             string
  gameID:             string
  storeID:            string
  title:              string
  internalName?:      string
  thumb?:             string
  salePrice:          string
  normalPrice:        string
  savings?:           string
  isOnSale?:          string
  metacriticScore?:   string
  metacriticLink?:    string
  steamRatingText?:   string
  steamRatingPercent?:string
  steamRatingCount?:  string
  steamAppID?:        string
  releaseDate?:       number
  lastChange?:        number
  dealRating?:        string
}

export interface StoreRow {
  storeID:   string
  storeName: string
  isActive:  number
  images?: {
    banner?: string
    logo?:   string
    icon?:   string
  }
}

export interface PriceHistoryPoint {
  recordedAt:  string
  salePrice:   number
  normalPrice: number
}

export interface PriceAlert {
  gameID:      string
  title?:      string
  targetPrice: number
  isActive?:   boolean
}

interface DbStoreRow {
  store_id:    string
  store_name:  string
  is_active:   boolean
  banner_path: string | null
  logo_path:   string | null
  icon_path:   string | null
  updated_at:  string
}

interface DbDealRow {
  deal_id:              string
  game_id:              string
  store_id:             string
  title:                string
  internal_name:        string | null
  thumb:                string | null
  sale_price:           number
  normal_price:         number
  savings:              number | null
  is_on_sale:           boolean
  metacritic_score:     number | null
  metacritic_link:      string | null
  steam_rating_text:    string | null
  steam_rating_percent: number | null
  steam_rating_count:   number | null
  steam_app_id:         string | null
  release_date:         string | null
  last_change:          string | null
  deal_rating:          number | null
  fetched_at:           string
}

interface DbWishlistRow {
  id:           string
  user_id:      string
  game_id:      string
  deal_id:      string | null
  title:        string
  thumb:        string | null
  store_id:     string | null
  normal_price: number | null
  sale_price:   number | null
  savings:      number | null
  created_at:   string
  updated_at:   string
}

// ── Singleton class ───────────────────────────────────────────────────────────

class SupabaseAPI {
  private static instance: SupabaseAPI
  public readonly client: SupabaseClient

  private constructor() {
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Missing VUE_APP_SUPABASE_URL or VUE_APP_SUPABASE_KEY in .env.local')
    }
    this.client = createClient(supabaseUrl, supabaseKey)
  }

  public static getInstance(): SupabaseAPI {
    if (!SupabaseAPI.instance) SupabaseAPI.instance = new SupabaseAPI()
    return SupabaseAPI.instance
  }

  // ── Auth ────────────────────────────────────────────────────────────────────

  async signInAnonymously() {
    const { data, error } = await this.client.auth.signInAnonymously()
    if (error) console.error('Anonymous sign-in error:', error)
    return { user: data?.user ?? null, session: data?.session ?? null, error }
  }

  async signInWithGoogle() {
    const { data, error } = await this.client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    })
    if (error) console.error('Google sign-in error:', error)
    return { data, error }
  }

  // Links an existing anonymous session to a Google identity (preserves user_id)
  async linkGoogleIdentity() {
    const { data, error } = await this.client.auth.linkIdentity({ provider: 'google' })
    if (error) console.error('Identity link error:', error)
    return { data, error }
  }

  async signOut() {
    const { error } = await this.client.auth.signOut()
    if (error) console.error('Sign-out error:', error)
    return { error }
  }

  async getSession(): Promise<Session | null> {
    const { data } = await this.client.auth.getSession()
    return data.session
  }

  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    return this.client.auth.onAuthStateChange(callback)
  }

  // ── Wishlist ─────────────────────────────────────────────────────────────────

  async fetchWishlist(userId: string): Promise<WishlistItem[]> {
    const { data, error } = await this.client
      .from('wishlist')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    if (error) {
      console.error('fetchWishlist error:', error)
      return []
    }
    return (data as DbWishlistRow[]).map(row => this.dbRowToWishlistItem(row))
  }

  async addWishlistItem(userId: string, item: WishlistItem): Promise<{ error: unknown }> {
    const { error } = await this.client
      .from('wishlist')
      .upsert(
        {
          user_id:      userId,
          game_id:      item.gameID,
          deal_id:      item.dealID || null,
          title:        item.title,
          thumb:        item.thumb || null,
          store_id:     item.storeID || null,
          normal_price: parseFloat(item.normalPrice) || null,
          sale_price:   parseFloat(item.salePrice)   || null,
          savings:      parseFloat(item.savings)      || null,
        },
        { onConflict: 'user_id,game_id' }
      )
    if (error) console.error('addWishlistItem error:', error)
    return { error }
  }

  async removeWishlistItem(userId: string, gameID: string): Promise<{ error: unknown }> {
    const { error } = await this.client
      .from('wishlist')
      .delete()
      .eq('user_id', userId)
      .eq('game_id', gameID)
    if (error) console.error('removeWishlistItem error:', error)
    return { error }
  }

  // Bulk upsert — used for migration of localStorage wishlist on first login
  async bulkUpsertWishlist(userId: string, items: WishlistItem[]): Promise<{ error: unknown }> {
    if (!items.length) return { error: null }
    const rows = items.map(item => ({
      user_id:      userId,
      game_id:      item.gameID,
      deal_id:      item.dealID || null,
      title:        item.title,
      thumb:        item.thumb || null,
      store_id:     item.storeID || null,
      normal_price: parseFloat(item.normalPrice) || null,
      sale_price:   parseFloat(item.salePrice)   || null,
      savings:      parseFloat(item.savings)      || null,
    }))
    const { error } = await this.client
      .from('wishlist')
      .upsert(rows, { onConflict: 'user_id,game_id', ignoreDuplicates: true })
    if (error) console.error('bulkUpsertWishlist error:', error)
    return { error }
  }

  // ── Deals / Stores (daily upsert) ────────────────────────────────────────────

  async upsertDeals(deals: DealRow[]): Promise<{ error: unknown }> {
    if (!deals.length) return { error: null }
    const rows = deals.map(d => ({
      deal_id:             d.dealID,
      game_id:             d.gameID,
      store_id:            d.storeID,
      title:               d.title,
      internal_name:       d.internalName || null,
      thumb:               d.thumb || null,
      sale_price:          parseFloat(d.salePrice)   || 0,
      normal_price:        parseFloat(d.normalPrice) || 0,
      savings:             d.savings      ? parseFloat(d.savings)      : null,
      is_on_sale:          d.isOnSale === '1',
      metacritic_score:    d.metacriticScore    ? parseInt(d.metacriticScore)    : null,
      metacritic_link:     d.metacriticLink     || null,
      steam_rating_text:   d.steamRatingText    || null,
      steam_rating_percent:d.steamRatingPercent ? parseInt(d.steamRatingPercent) : null,
      steam_rating_count:  d.steamRatingCount   ? parseInt(d.steamRatingCount)   : null,
      steam_app_id:        d.steamAppID         || null,
      release_date:        d.releaseDate ? new Date(d.releaseDate * 1000).toISOString() : null,
      last_change:         d.lastChange  ? new Date(d.lastChange  * 1000).toISOString() : null,
      deal_rating:         d.dealRating  ? parseFloat(d.dealRating) : null,
      fetched_at:          new Date().toISOString(),
    }))
    const { error } = await this.client
      .from('deals')
      .upsert(rows, { onConflict: 'deal_id' })
    if (error) console.error('upsertDeals error:', error)
    return { error }
  }

  async upsertStores(stores: StoreRow[]): Promise<{ error: unknown }> {
    if (!stores.length) return { error: null }
    const rows = stores.map(s => ({
      store_id:    String(s.storeID),
      store_name:  s.storeName,
      is_active:   s.isActive === 1,
      banner_path: s.images?.banner || null,
      logo_path:   s.images?.logo   || null,
      icon_path:   s.images?.icon   || null,
      updated_at:  new Date().toISOString(),
    }))
    const { error } = await this.client
      .from('stores')
      .upsert(rows, { onConflict: 'store_id' })
    if (error) console.error('upsertStores error:', error)
    return { error }
  }

  // ── Deals / Stores (read — app's canonical data source) ──────────────────────

  async fetchStores(): Promise<StoreRow[]> {
    const { data, error } = await this.client
      .from('stores')
      .select('*')
      .order('store_id', { ascending: true })
    if (error) {
      console.error('fetchStores error:', error)
      return []
    }
    return (data as DbStoreRow[]).map(row => this.dbRowToStore(row))
  }

  async fetchDeals(): Promise<DealRow[]> {
    const { data, error } = await this.client
      .from('deals')
      .select('*')
      .order('deal_rating', { ascending: false, nullsFirst: false })
    if (error) {
      console.error('fetchDeals error:', error)
      return []
    }
    return (data as DbDealRow[]).map(row => this.dbRowToDeal(row))
  }

  // Detail page: reconstruct the CheapShark gameDealLookup shape from Supabase.
  // Returns { gameInfo, cheaperStores } or null if the deal is unknown.
  async fetchDealLookup(dealID: string): Promise<{ gameInfo: any; cheaperStores: any[] } | null> {
    const { data: deal, error } = await this.client
      .from('deals')
      .select('*')
      .eq('deal_id', dealID)
      .maybeSingle()
    if (error || !deal) {
      if (error) console.error('fetchDealLookup error:', error)
      return null
    }
    const row = deal as DbDealRow

    // Sibling deals for the same game, cheaper than this one → "other stores".
    const { data: siblings } = await this.client
      .from('deals')
      .select('*')
      .eq('game_id', row.game_id)
      .lt('sale_price', row.sale_price)
      .order('sale_price', { ascending: true })

    const cheaperStores = ((siblings as DbDealRow[]) || []).map(s => ({
      dealID:    s.deal_id,
      storeID:   s.store_id,
      salePrice: String(s.sale_price),
      savings:   s.savings != null ? String(s.savings) : '0',
    }))

    const gameInfo = {
      gameID:             row.game_id,
      storeID:            row.store_id,
      name:               row.title,
      thumb:              row.thumb ?? '',
      salePrice:          String(row.sale_price),
      retailPrice:        String(row.normal_price),
      steamRatingText:    row.steam_rating_text ?? undefined,
      steamRatingPercent: row.steam_rating_percent != null ? String(row.steam_rating_percent) : undefined,
      steamRatingCount:   row.steam_rating_count   != null ? String(row.steam_rating_count)   : undefined,
      metacriticScore:    row.metacritic_score     != null ? String(row.metacritic_score)     : undefined,
      metacriticLink:     row.metacritic_link      ?? undefined,
      steamAppID:         row.steam_app_id         ?? undefined,
      releaseDate:        row.release_date ? Math.floor(new Date(row.release_date).getTime() / 1000) : undefined,
      dealRating:         row.deal_rating != null ? String(row.deal_rating) : undefined,
      // publisher and cheapestPriceEver are not stored; the template guards both with v-if.
    }

    return { gameInfo, cheaperStores }
  }

  async fetchPriceHistory(gameID: string, storeID: string): Promise<PriceHistoryPoint[]> {
    const { data, error } = await this.client
      .from('price_history')
      .select('recorded_at, sale_price, normal_price')
      .eq('game_id', gameID)
      .eq('store_id', storeID)
      .order('recorded_at', { ascending: true })
    if (error) {
      console.error('fetchPriceHistory error:', error)
      return []
    }
    return (data || []).map((row: any) => ({
      recordedAt:  row.recorded_at as string,
      salePrice:   Number(row.sale_price),
      normalPrice: Number(row.normal_price),
    }))
  }

  // ── Price alerts ─────────────────────────────────────────────────────────────

  async getPriceAlerts(userId: string): Promise<PriceAlert[]> {
    const { data, error } = await this.client
      .from('price_alerts')
      .select('*')
      .eq('user_id', userId)
    if (error) {
      console.error('getPriceAlerts error:', error)
      return []
    }
    return (data || []).map((row: any) => ({
      gameID:      row.game_id,
      title:       row.title,
      targetPrice: row.target_price,
      isActive:    row.is_active,
    }))
  }

  async upsertPriceAlert(userId: string, alert: PriceAlert): Promise<{ error: unknown }> {
    const { error } = await this.client
      .from('price_alerts')
      .upsert(
        {
          user_id:      userId,
          game_id:      alert.gameID,
          title:        alert.title || null,
          target_price: alert.targetPrice,
          is_active:    alert.isActive ?? true,
        },
        { onConflict: 'user_id,game_id' }
      )
    if (error) console.error('upsertPriceAlert error:', error)
    return { error }
  }

  async deletePriceAlert(userId: string, gameID: string): Promise<{ error: unknown }> {
    const { error } = await this.client
      .from('price_alerts')
      .delete()
      .eq('user_id', userId)
      .eq('game_id', gameID)
    if (error) console.error('deletePriceAlert error:', error)
    return { error }
  }

  // ── User profile ─────────────────────────────────────────────────────────────

  async getProfile(userId: string) {
    const { data, error } = await this.client
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single()
    if (error) console.error('getProfile error:', error)
    return data || null
  }

  // Store the price-alert notification address on the user's profile.
  //
  // This is an upsert so it self-heals a missing profile row: the row is
  // normally created by the fn_create_user_profile signup trigger, but a user
  // who signed up before that trigger existed (or whose insert did not fire it)
  // has no row, and a plain UPDATE would match zero rows and leave the alert
  // with nowhere to email. The upsert's INSERT path is allowed by the
  // profiles_insert_own RLS policy (WITH CHECK auth.uid() = user_id, migration
  // 003); on conflict it updates alert_email/updated_at on the existing row.
  async updateAlertEmail(userId: string, email: string): Promise<{ error: unknown }> {
    const { error } = await this.client
      .from('user_profiles')
      .upsert(
        { user_id: userId, alert_email: email, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      )
    if (error) console.error('updateAlertEmail error:', error)
    return { error }
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  private dbRowToStore(row: DbStoreRow): StoreRow {
    return {
      storeID:   row.store_id,
      storeName: row.store_name,
      isActive:  row.is_active ? 1 : 0,
      images: {
        banner: row.banner_path ?? undefined,
        logo:   row.logo_path ?? undefined,
        icon:   row.icon_path ?? undefined,
      },
    }
  }

  // Emit the CheapShark-style (camelCase, stringified numerics) shape the views/Vuex expect.
  private dbRowToDeal(row: DbDealRow): DealRow {
    return {
      dealID:             row.deal_id,
      gameID:             row.game_id,
      storeID:            row.store_id,
      title:              row.title,
      internalName:       row.internal_name ?? undefined,
      thumb:              row.thumb ?? undefined,
      salePrice:          String(row.sale_price),
      normalPrice:        String(row.normal_price),
      savings:            row.savings != null ? String(row.savings) : undefined,
      isOnSale:           row.is_on_sale ? '1' : '0',
      metacriticScore:    row.metacritic_score != null ? String(row.metacritic_score) : undefined,
      metacriticLink:     row.metacritic_link ?? undefined,
      steamRatingText:    row.steam_rating_text ?? undefined,
      steamRatingPercent: row.steam_rating_percent != null ? String(row.steam_rating_percent) : undefined,
      steamRatingCount:   row.steam_rating_count != null ? String(row.steam_rating_count) : undefined,
      steamAppID:         row.steam_app_id ?? undefined,
      releaseDate:        row.release_date ? Math.floor(new Date(row.release_date).getTime() / 1000) : undefined,
      lastChange:         row.last_change ? Math.floor(new Date(row.last_change).getTime() / 1000) : undefined,
      dealRating:         row.deal_rating != null ? String(row.deal_rating) : undefined,
    }
  }

  private dbRowToWishlistItem(row: DbWishlistRow): WishlistItem {
    return {
      gameID:      row.game_id,
      title:       row.title,
      thumb:       row.thumb      ?? '',
      storeID:     row.store_id   ?? '',
      normalPrice: row.normal_price != null ? String(row.normal_price) : '0',
      salePrice:   row.sale_price   != null ? String(row.sale_price)   : '0',
      savings:     row.savings      != null ? String(row.savings)      : '0',
      dealID:      row.deal_id      ?? '',
    }
  }
}

export default SupabaseAPI.getInstance()
